/* Sign in — the console's sign-in card (SignIn.module.css is its copy). An
 * email and a password (000082): the server issues an AGENCY_DESKTOP session
 * from a password only, never from an OTP, and agency accounts have no mobile
 * number.
 *
 * Forgot password (2026-10-29, 000094): the server says which form (GET
 * /auth/password/reset-mode). For now it is "direct" (the owner's choice until
 * email is set up): the email and a new password, set at once (POST
 * /auth/password/reset-direct). Once email is on it is "email": a link is
 * emailed (POST /auth/password/forgot) and opens this page with
 * ?reset=<token>, which shows "Set a new password" (POST /auth/password/reset).
 * "off": ask the platform team, who reset it from the console. Password fields
 * have Show. */

import { useEffect, useState, type FormEvent } from 'react';
import { directResetPassword, forgotPassword, getResetMode, resetPassword, signIn, signOut, type ResetMode } from '../data/api';
import { errorText } from '../data/words';
import { Btn, Field, Note, PasswordInput, uiCss } from '../ui';
import css from './SignIn.module.css';

/** +91 by default: people type their ten-digit number. Used for the agency's
 *  contact phone at registration — not for signing in. */
export function toE164(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.length === 10) return '+91' + digits;
  if (digits.length === 12 && digits.startsWith('91')) return '+' + digits;
  return digits;
}

/** The token of a "set a new password" link, when this page was opened from one. */
export function resetTokenInUrl(): string | null {
  const t = new URLSearchParams(window.location.search).get('reset');
  return t && /^[0-9a-f]{64}$/.test(t) ? t : null;
}

/** Drop ?reset= from the address bar, so a reload or a shared screen does not carry it. */
function clearResetFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('reset');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
}

export function Brand({ sub }: { sub: string }) {
  return (
    <div className={css.head}>
      <div className={css.brand}>
        <div className={css.brandMark} aria-hidden="true">
          JD
        </div>
        <div className={css.brandText}>
          <span className={css.brandName}>Agency Desk</span>
          <span className={css.brandSub}>{sub}</span>
        </div>
      </div>
    </div>
  );
}

function Heading({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <div className={css.brandName} style={{ fontSize: 18 }}>
        {title}
      </div>
      <div className={css.brandSub} style={{ letterSpacing: 0, textTransform: 'none', fontSize: 12.5, marginTop: 4 }}>
        {sub}
      </div>
    </div>
  );
}

type Mode = 'signin' | 'forgot' | 'forgotSent' | 'reset' | 'resetDone';

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INPUT = `${uiCss.input} ${uiCss.inputLg}`;
const PRIMARY = `${uiCss.btn} ${uiCss.btnPrimary} ${uiCss.btnBlock}`;

export function SignIn({ onRegister, notice }: { onRegister: () => void; notice?: string | null }) {
  const [resetToken] = useState(resetTokenInUrl);
  const [mode, setMode] = useState<Mode>(resetToken ? 'reset' : 'signin');
  const [resetMode, setResetMode] = useState<ResetMode | null>(null);
  useEffect(() => {
    getResetMode()
      .then(setResetMode)
      .catch(() => setResetMode('off'));
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTo = (next: Mode) => {
    setMode(next);
    setError(null);
    setPassword('');
    setPassword2('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (mode === 'forgot' && !EMAIL.test(email.trim())) {
      setError('Enter the email you sign in with.');
      return;
    }
    if (mode === 'reset' || (mode === 'forgot' && resetMode === 'direct')) {
      if (password.length < 8) {
        setError('Use at least 8 characters for the password.');
        return;
      }
      if (password !== password2) {
        setError('The two passwords are not the same.');
        return;
      }
    }
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email.trim(), password);
      } else if (mode === 'forgot' && resetMode === 'direct') {
        const msg = await directResetPassword(email.trim(), password);
        signOut();
        setMessage(msg);
        switchTo('resetDone');
      } else if (mode === 'forgot' && resetMode === 'email') {
        setMessage(await forgotPassword(email.trim()));
        switchTo('forgotSent');
      } else if (mode === 'reset' && resetToken) {
        const msg = await resetPassword(resetToken, password);
        // Every session of the account was signed out; drop this browser's too.
        signOut();
        clearResetFromUrl();
        setMessage(msg);
        switchTo('resetDone');
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={css.page}>
      <form className={css.card} onSubmit={submit}>
        <Brand sub="Hospitals & care agencies" />
        <div className={css.body}>
          {notice && mode === 'signin' ? <Note tone="amber">{notice}</Note> : null}

          {mode === 'signin' ? (
            <>
              <Heading
                title="Sign in"
                sub="Take care requests from families near you and send your own nurses, doctors and ambulances."
              />
              <Field label="Email">
                <input
                  className={INPUT}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  aria-label="Email"
                  required
                />
              </Field>
              <Field label="Password">
                <PasswordInput value={password} onChange={setPassword} size="lg" ariaLabel="Password" autoComplete="current-password" />
              </Field>
              {error ? <Note tone="danger">{error}</Note> : null}
              <button type="submit" className={PRIMARY} disabled={busy || email.trim().length === 0 || password.length === 0}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <Btn block disabled={busy} onClick={() => switchTo('forgot')}>
                Forgot password?
              </Btn>
              <Btn block onClick={onRegister}>
                Register your agency
              </Btn>
            </>
          ) : null}

          {mode === 'forgot' && resetMode === 'off' ? (
            <>
              <Heading title="Forgot password" sub="Ask the platform team to reset it. They give you a one-time password; sign in with it, then change it under your name (top right)." />
              <Btn block onClick={() => switchTo('signin')}>
                Back to sign in
              </Btn>
            </>
          ) : null}

          {mode === 'forgot' && resetMode === 'direct' ? (
            <>
              <Heading title="Reset password" sub="Enter the email you sign in with and choose a new password." />
              <Field label="Email">
                <input
                  className={INPUT}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  aria-label="Email"
                  required
                />
              </Field>
              <Field label="New password (at least 8 characters)">
                <PasswordInput value={password} onChange={setPassword} size="lg" ariaLabel="New password" autoComplete="new-password" />
              </Field>
              <Field label="New password again">
                <PasswordInput value={password2} onChange={setPassword2} size="lg" ariaLabel="New password again" autoComplete="new-password" />
              </Field>
              {error ? <Note tone="danger">{error}</Note> : null}
              <button type="submit" className={PRIMARY} disabled={busy || email.trim().length === 0 || !password || !password2}>
                {busy ? 'Saving…' : 'Save new password'}
              </button>
              <Btn block disabled={busy} onClick={() => switchTo('signin')}>
                Back to sign in
              </Btn>
            </>
          ) : null}

          {mode === 'forgot' && (resetMode === 'email' || resetMode === null) ? (
            <>
              <Heading title="Forgot password" sub="Enter the email you sign in with. We will email you a link to choose a new password." />
              <Field label="Email">
                <input
                  className={INPUT}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  aria-label="Email"
                  required
                />
              </Field>
              {error ? <Note tone="danger">{error}</Note> : null}
              <button type="submit" className={PRIMARY} disabled={busy || email.trim().length === 0 || resetMode === null}>
                {busy ? 'Sending…' : 'Email me a link'}
              </button>
              <Btn block disabled={busy} onClick={() => switchTo('signin')}>
                Back to sign in
              </Btn>
            </>
          ) : null}

          {mode === 'forgotSent' ? (
            <>
              <Note tone="teal">{message}</Note>
              <div className={css.brandSub} style={{ letterSpacing: 0, textTransform: 'none', fontSize: 12.5 }}>
                No email? Check spam, or ask the platform team to reset your password.
              </div>
              <Btn variant="primary" block onClick={() => switchTo('signin')}>
                Back to sign in
              </Btn>
            </>
          ) : null}

          {mode === 'reset' ? (
            <>
              <Heading title="Set a new password" sub="Choose a new password for the agency desk. You will then sign in with it." />
              <Field label="New password (at least 8 characters)">
                <PasswordInput value={password} onChange={setPassword} size="lg" ariaLabel="New password" autoComplete="new-password" />
              </Field>
              <Field label="New password again">
                <PasswordInput value={password2} onChange={setPassword2} size="lg" ariaLabel="New password again" autoComplete="new-password" />
              </Field>
              {error ? <Note tone="danger">{error}</Note> : null}
              <button type="submit" className={PRIMARY} disabled={busy || !password || !password2}>
                {busy ? 'Saving…' : 'Save new password'}
              </button>
              <Btn
                block
                disabled={busy}
                onClick={() => {
                  clearResetFromUrl();
                  switchTo('forgot');
                }}
              >
                Link not working? Ask for a new one
              </Btn>
            </>
          ) : null}

          {mode === 'resetDone' ? (
            <>
              <Note tone="teal">{message}</Note>
              <Btn variant="primary" block onClick={() => switchTo('signin')}>
                Sign in
              </Btn>
            </>
          ) : null}
        </div>
        <div className={css.foot}>
          An offer shows only the area and the care asked for. A patient's name and address reach you when your agency has
          accepted the job, and every time they are opened it is recorded.
        </div>
      </form>
    </div>
  );
}
